import {
  BotDetectAttributes,
  FeedbackAttributes,
  RatingAttributes,
  WogaaLayerActions,
} from '@actions/WogaaLayerActions';
import { CustomWindow } from '@entities/CustomWindow';
import { EnvConfigs, Version } from '@environment/resources';

export interface WogaaCustomEvent {
  category: string;
  action: string;
  label?: string;
  property?: string;
  value?: number;
}

interface EventContext {
  transactionID: string; // sentiments id
  rsid: string; // report suite id
  transactionServiceID?: string; // tracking Id
  agencyTransactionID?: string;
  questionConfigId: number;
  variant?: string
}

interface CustomEventContext {
  user: string;
  category: string;
  action: string;
  label: string;
  property: string;
  value: any;
  content: object;
}

declare let window: CustomWindow;

export default class SnowplowService {
  dispatch(event: WogaaLayerActions) {
    window.wogaaLayer.push(event);
  }

  getContext(
    rsid: string,
    sentimentsId: string,
    transactionServiceID: string,
    questionConfigId: number,
    variant?: string,
  ): { schema: string; data: EventContext } {
    const { agencyTxnId: agencyTransactionID } = window.digitalData || {};
    return {
      schema: EnvConfigs.SnowplowContext,
      data: {
        rsid,
        transactionServiceID,
        agencyTransactionID,
        transactionID: sentimentsId,
        questionConfigId,
        variant,
      },
    };
  }

  getCustomContext(
    sentimentsId: string,
    category: string,
    action: string,
    label: string,
    property: string,
    value: any,
    content: object,
  ): { schema: string; data: CustomEventContext } {
    return {
      schema: EnvConfigs.SnowplowCustomContext,
      data: {
        user: sentimentsId,
        category,
        action,
        label,
        property,
        value,
        content,
      },
    };
  }

  submitRatingEvent(ratingAttributes: RatingAttributes) {
    const { rating, rsid, ratingType, variant, sentimentsId, trackingId, questionConfigId, ratingScaleDesign } =
      ratingAttributes;

    window.wogaa_snowplow('trackSelfDescribingEvent:wog', {
      event: {
        schema: EnvConfigs.SnowplowRatingEvent,
        data: {
          rating,
          ratingScaleDesign,
          ratingType,
        },
      },
      context: [this.getContext(rsid, sentimentsId, trackingId, questionConfigId, variant)],
    });
  }

  submitFeedbackEvent(feedbackAttributes: FeedbackAttributes) {
    const { rsid, variant, sentimentsId, trackingId, questionConfigId, ...props } = feedbackAttributes;

    window.wogaa_snowplow('trackSelfDescribingEvent:wog', {
      event: {
        schema: EnvConfigs.SnowplowFeedbackEvent,
        data: props,
      },
      context: [this.getContext(rsid, sentimentsId, trackingId, questionConfigId, variant)],
    });
  }

  submitBotDetectEvent(botAttributes: BotDetectAttributes) {
    const { sentimentsId, label, value, content } = botAttributes;

    window.wogaa_snowplow('trackSelfDescribingEvent', {
      event: {
        schema: EnvConfigs.SnowplowCustomEvent,
        data: {
          project: 'wogaa',
          projectVersion: Version,
        },
      },
      context: [
        this.getCustomContext(
          sentimentsId,
          'bot-detection',
          'sentiment-submission',
          label,
          'isBot',
          value,
          content,
        ),
      ],
    });
  }

  setUserId(id: string) {
    window.wogaa_snowplow('setUserId', id);
  }

  /**
   * Custom event tracking
   * @param customEvent
   */
  customTrackEvent(customEvent: WogaaCustomEvent) {
    const { category, action, label, property, value } = customEvent;
    window.wogaa_snowplow('trackStructEvent:wog', { category, action, label, property, value });
  }
}
